import { Footer, Navbar } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
    getListCoursesQueryKey,
    useCreateAppointment,
    useListCourses,
} from '@/hooks/use-api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import {
    ArrowRight,
    CalendarIcon,
    CheckCircle2,
    Home,
    Loader2,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'wouter';
import * as z from 'zod';

const formSchema = z.object({
  courseId: z.string().min(1, 'Please select a course'),
  userName: z.string().min(2, 'Name must be at least 2 characters'),
  userEmail: z.string().email('Invalid email address'),
  userPhone: z.string().optional(),
  date: z.date({
    required_error: 'A date is required.',
  }),
  time: z.string().min(1, 'Please select a time'),
  notes: z.string().optional(),
});

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any },
  },
};

export default function Book() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);

  const { data: allCourses, isLoading: isLoadingCourses } = useListCourses({
    query: { queryKey: getListCoursesQueryKey() },
  });

  // Filter to show only active courses for booking
  const courses = allCourses?.filter(course => course.active) || [];

  const createAppointment = useCreateAppointment();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      courseId: '',
      userName: '',
      userEmail: '',
      userPhone: '',
      time: '09:00',
      notes: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const selectedCourse = courses?.find(
      c => c.id.toString() === values.courseId
    );
    if (!selectedCourse) return;

    // Combine date and time
    const [hours, minutes] = values.time.split(':');
    const scheduledAt = new Date(values.date);
    scheduledAt.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    try {
      await createAppointment.mutateAsync({
        data: {
          courseId: selectedCourse.id,
          courseTitle: selectedCourse.title,
          price: selectedCourse.price,
          userName: values.userName,
          userEmail: values.userEmail,
          userPhone: values.userPhone || null,
          scheduledAt: scheduledAt.toISOString(),
          notes: values.notes || null,
        },
      });

      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast({
        title: 'Booking Failed',
        description:
          'There was an error submitting your booking. Please try again.',
        variant: 'destructive',
      });
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <Navbar />
        <main className="flex-1 flex items-center justify-center py-24 sm:py-28 px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <Card className="max-w-md w-full text-center border-slate-200 dark:border-slate-800 shadow-xl">
              <CardContent className="pt-10 pb-8 px-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.2,
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                  }}
                  className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-500" />
                </motion.div>
                <h2 className="font-bebas text-3xl sm:text-4xl tracking-wide mb-4">
                  BOOKING CONFIRMED
                </h2>
                <p className="font-sans text-slate-600 dark:text-slate-400 mb-8 text-sm sm:text-base">
                  Your training session has been scheduled. We've sent a
                  confirmation email with all the details and preparation
                  requirements.
                </p>
                <div className="flex flex-col gap-3">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      className="font-rajdhani font-bold tracking-wider w-full"
                      onClick={() => setLocation('/dashboard')}
                    >
                      GO TO DASHBOARD
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline"
                      className="font-rajdhani font-bold tracking-wider w-full"
                      onClick={() => setLocation('/')}
                    >
                      <Home className="mr-2 w-4 h-4" />
                      RETURN HOME
                    </Button>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <Navbar />

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="mb-8 sm:mb-10 text-center"
        >
          <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-wide mb-4">
            BOOK A SESSION
          </h1>
          <p className="font-sans text-slate-500 dark:text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            Select your course and preferred date to secure your spot.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Card className="border-slate-200 dark:border-slate-800 shadow-xl">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="font-rajdhani font-bold tracking-wider text-lg sm:text-xl">
                TRAINING DETAILS
              </CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Fill out the form below to schedule your high-rise training.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="courseId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-rajdhani font-bold tracking-wider text-sm">
                          SELECT COURSE
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-12 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-base">
                              <SelectValue placeholder="Choose a training program..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {isLoadingCourses ? (
                              <div className="p-4 flex justify-center">
                                <Loader2 className="w-5 h-5 animate-spin" />
                              </div>
                            ) : (
                              courses?.map(course => (
                                <SelectItem
                                  key={course.id}
                                  value={course.id.toString()}
                                  className="text-base"
                                >
                                  {course.title} - ${course.price}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel className="font-rajdhani font-bold tracking-wider mt-2 text-sm">
                            DATE
                          </FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={'outline'}
                                  className={cn(
                                    'h-12 pl-3 text-left font-normal bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-base',
                                    !field.value && 'text-muted-foreground'
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, 'PPP')
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={date =>
                                  date < new Date() ||
                                  date.getDay() === 0 ||
                                  date.getDay() === 6
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="time"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-rajdhani font-bold tracking-wider text-sm">
                            TIME
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="h-12 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-base">
                                <SelectValue placeholder="Select time..." />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="09:00" className="text-base">
                                09:00 AM (Morning Session)
                              </SelectItem>
                              <SelectItem value="13:00" className="text-base">
                                01:00 PM (Afternoon Session)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="border-t border-slate-200 dark:border-slate-800 my-6 pt-6">
                    <h3 className="font-rajdhani font-bold tracking-wider text-lg sm:text-xl mb-6">
                      CONTACT INFORMATION
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                      <FormField
                        control={form.control}
                        name="userName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-rajdhani font-bold tracking-wider text-sm">
                              FULL NAME
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="John Doe"
                                className="h-12 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-base"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="userPhone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-rajdhani font-bold tracking-wider text-sm">
                              PHONE NUMBER
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="(555) 123-4567"
                                className="h-12 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-base"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="userEmail"
                      render={({ field }) => (
                        <FormItem className="mb-6">
                          <FormLabel className="font-rajdhani font-bold tracking-wider text-sm">
                            EMAIL ADDRESS
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="john@example.com"
                              type="email"
                              className="h-12 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-base"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-rajdhani font-bold tracking-wider text-sm">
                            ADDITIONAL NOTES (OPTIONAL)
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Any previous experience or special requirements..."
                              className="resize-none h-24 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-base"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="pt-4">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full h-14 font-rajdhani font-bold tracking-wider text-base sm:text-lg"
                        disabled={createAppointment.isPending}
                      >
                        {createAppointment.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />{' '}
                            SUBMITTING...
                          </>
                        ) : (
                          <>
                            CONFIRM BOOKING{' '}
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
